import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import PageHeader from "../../Components/UI/PageHeader";
import SectionCard from "../../Components/UI/SectionCard";
import { API, ADMIN_API } from "../../Utils/API";

const KPI = ({ label, value, hint }) => (
  <Paper sx={{ p: 2, borderRadius: 3, height: "100%" }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Paper>
);

const Admin = () => {
  const [qrModal, setQRModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.post(ADMIN_API.CONSOLE_OVERVIEW);
      setOverview(res.data?.data || null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to load admin overview");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const banks = overview?.banks || [];

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <PageHeader
        title="Admin Console"
        subtitle="Partner banks, SEC@R adoption, and bank customer totals."
        actions={[
          {
            label: "Refresh",
            key: "refresh",
            onClick: loadOverview,
            color: "primary",
            variant: "outlined",
          },
          {
            label: "Add admin user",
            key: "add",
            onClick: () => setQRModal(true),
            color: "primary",
            variant: "filled",
          },
        ]}
      />

      {error && (
        <SectionCard title="Error">
          <Typography color="error">{error}</Typography>
        </SectionCard>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KPI
            label="SEC@R Users (total)"
            value={loading ? "…" : overview?.secar_users_total ?? 0}
            hint="Registered, not archived"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPI
            label="SEC@R Users (verified)"
            value={loading ? "…" : overview?.secar_users_verified ?? 0}
            hint="Using SEC@R solution"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPI
            label="Partner Banks Online"
            value={
              loading
                ? "…"
                : `${overview?.bank_partners_online ?? 0} / ${overview?.bank_partners_total ?? 0}`
            }
            hint="Banks integrated with SEC@R"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPI
            label="Total Bank Customers"
            value={loading ? "…" : overview?.total_bank_customers ?? 0}
            hint="Across all partner banks"
          />
        </Grid>
      </Grid>

      <SectionCard title="Partner Banks">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Bank</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>SEC@R Partner</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Customers</TableCell>
                <TableCell align="right">Accounts</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6}>Loading…</TableCell>
                </TableRow>
              )}
              {!loading && banks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>No partner banks configured.</TableCell>
                </TableRow>
              )}
              {!loading &&
                banks.map((bank) => (
                  <TableRow key={bank.code}>
                    <TableCell>{bank.name}</TableCell>
                    <TableCell>{bank.code}</TableCell>
                    <TableCell>
                      <Chip size="small" color="primary" label="Yes" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={bank.online ? "success" : "default"}
                        label={bank.online ? "Online" : "Offline"}
                      />
                    </TableCell>
                    <TableCell align="right">{bank.customer_count}</TableCell>
                    <TableCell align="right">{bank.account_count}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={qrModal}
        onClose={() => setQRModal(false)}
        aria-labelledby="alert-dialog-title"
      >
        <DialogTitle id="alert-dialog-title">Invite Admin User</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <TextField fullWidth placeholder="Email" label="Admin Email" variant="outlined" type="email" size="small" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" variant="contained" onClick={() => {}}>
            Send Invite
          </Button>
          <Button size="small" variant="outlined" onClick={() => setQRModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admin;
